import React, { useEffect, useMemo, useState } from "react";
import { useQuery } from "react-query";
import { useSchedulingQuery } from "../../../../hooks/ReactQueryHooks/useSchedulingQuery";
import { axios } from "../../../../lib/axios";
import { decryptData } from "../../../EncryptDecrypt";
import { practitionerIdFromUser, seedPractitionerName, displayPractitionerName } from "../names";
import { listAllSchedules } from "../../../api/Scheduling/schedules";
import OverviewMultiSelect from "./OverviewMultiSelect";

export default function PractitionerFilter({ organizationRef, options, value, onChange }) {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const [search, setSearch] = useState("");
  const [known, setKnown] = useState([]);
  useEffect(() => { const timer = setTimeout(() => setSearch(input.trim()), 300); return () => clearTimeout(timer); }, [input]);
  useEffect(() => { setKnown([]); setInput(""); setSearch(""); }, [organizationRef]);
  useEffect(() => { setKnown(current => [...new Map([...current, ...options].map(option => [option.value, option])).values()]); }, [options]);
  const query = useQuery(["scheduling-practitioner-search", organizationRef, search], async ({ signal }) => {
    const result = await axios.get("users", { params: { orgId: organizationRef, type: "Practitioner", active: true, limit: 20, pageNumber: 1, searchQuery: search }, signal });
    return (result?.data || []).flatMap(row => {
      const ref = practitionerIdFromUser(row);
      if (!ref) return [];
      const label = decryptData(row?.name?.[0]?.text || "");
      if (!label || label === ref || label === "Unknown") return [];
      seedPractitionerName(ref, label);
      return [{ value: ref, label }];
    });
  }, { enabled: open && !!organizationRef && input.trim().length >= 2 && search === input.trim(), staleTime: 60000, refetchOnWindowFocus: false, retry: false });
  const suppliers = useSchedulingQuery(["scheduling-team-filter", organizationRef], ({ signal, consistency }) => listAllSchedules({ organizationRef, scope: "team", limit: 100, signal, consistency }),
    { enabled: open && !!organizationRef, staleTime: 30000, refetchOnWindowFocus: false, retry: false });
  const searching = input.trim().length >= 2;
  const choices = useMemo(() => {
    const parents = (suppliers.data || []).map(row => ({ value: row.practitionerRef,
      label: `${displayPractitionerName({ name: row.practitionerName })} · Parent · ${row.organizationName || ""}` }));
    const local = [...known, ...options, ...parents].filter(option => option.label !== "Practitioner" && option.label.toLocaleLowerCase().includes(input.trim().toLocaleLowerCase()));
    const remote = searching && search === input.trim() ? query.data || [] : [];
    return [...new Map([...local, ...remote].map(option => [option.value, option])).values()].sort((a, b) => a.label.localeCompare(b.label));
  }, [known, options, input, search, searching, query.data, suppliers.data]);
  return <OverviewMultiSelect inputId="overview-practitioner" options={choices} value={value} allLabel="All practitioners" placeholder="Search practitioners…"
    onChange={onChange} remoteSearch
    onOpenChange={setOpen} onSearchChange={setInput} loading={searching && (search !== input.trim() || query.isFetching)} searchError={searching && search === input.trim() && query.isError} />;
}
